import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { LATAM_COUNTRIES } from '../../utils/market';
import LatamCurrencies from './LatamCurrencies';
import LatamBasePicker from './LatamBasePicker';

/**
 * Ajustes do LATAM num botão só, ao lado do seletor de mercado: mostra as
 * bandeiras das moedas aceitas e a moeda dos valores; o clique abre o painel
 * com "Moedas aceitas" e "Valores em". Só visual, sem API.
 */
export default function LatamSettings({ available, currencies, onCurrenciesChange, base, onBaseChange, saving = false }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
        };
    }, [open]);

    // Só o que o bot aceita E algum gateway conectado cobra (XPag, NXGate...).
    const flags = LATAM_COUNTRIES.filter((c) => currencies.includes(c.currency) && (available || []).includes(c.currency));
    const nothingChargeable = (available || []).length > 0 && flags.length === 0;
    // Uma moeda só não tem o que escolher, a não ser que a marcada não seja cobrável.
    const pickCurrencies = (available || []).length > 1 || nothingChargeable;

    return (
        <div ref={ref} className="relative inline-block">
            <button
                type="button"
                aria-label="Ajustes do LATAM"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 h-8 pl-2 pr-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-[11px] font-medium text-white/80 hover:text-white hover:bg-white/[0.06] transition-colors"
            >
                <span className="inline-flex -space-x-1" aria-hidden="true">
                    {flags.map((c) => (
                        <img key={c.currency} alt="" className="w-4 h-3 object-cover rounded-[2px] ring-1 ring-black/40"
                            src={`https://flagcdn.com/w20/${c.flag}.png`} />
                    ))}
                </span>
                {nothingChargeable && (
                    <span role="alert" className="text-amber-300">Nenhuma moeda marcada é cobrada pelos seus gateways</span>
                )}
                <span className="text-white/40">·</span>
                <span>{base}</span>
                <ChevronDown size={12} className={`opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div role="dialog" aria-label="Ajustes do LATAM"
                    className="absolute right-0 sm:left-0 sm:right-auto top-full mt-1.5 z-50 w-[19rem] rounded-xl border border-white/10 bg-[#10141a] shadow-[0_16px_48px_rgba(0,0,0,0.55)] p-3 text-white">
                    {pickCurrencies && (
                        <section className="mb-3 pb-3 border-b border-white/[0.06]">
                            <p className="text-xs font-semibold mb-0.5">Moedas aceitas</p>
                            <p className="text-[11px] text-white/45 mb-2">O comprador escolhe entre as marcadas.</p>
                            <LatamCurrencies available={available} value={currencies} onChange={onCurrenciesChange} saving={saving} />
                        </section>
                    )}
                    <section>
                        <p className="text-xs font-semibold mb-0.5">Valores em</p>
                        <p className="text-[11px] text-white/45 mb-2">Moeda dos planos, ofertas e disparos. O comprador paga convertido.</p>
                        <LatamBasePicker value={base} onChange={onBaseChange} saving={saving} />
                    </section>
                </div>
            )}
        </div>
    );
}
